import { useCallback, useEffect, useMemo, useState } from "react";
import {
  api,
  ApiError,
  type ClimaConvite,
  type ClimaPergunta,
  type ClimaPesquisa,
  type ClimaResposta,
} from "../api";
import type { Row } from "../types";
import {
  CLIMA_ESCALA,
  CLIMA_PERGUNTAS,
  classificacaoClima,
  rotuloEscala,
} from "../clima-modelo";
import {
  IconCheck,
  IconClose,
  IconCopy,
  IconLink,
  IconPlus,
  IconSearch,
  IconTrash,
} from "../icons";
import { usePodeExcluir } from "../permissoes";
import { compararNomes, ordenarPorNome, ordenarTextos } from "../ordem";

/**
 * PESQUISA DE CLIMA — a pesquisa aberta pelo RH.
 *
 * Quatro abas, na ordem em que o trabalho acontece:
 *   Pesquisa   → título, período, sigilo e as perguntas (o questionário oficial)
 *   Enviar     → gera o LINK DE CADA COLABORADOR e acompanha quem já respondeu
 *   Respostas  → cada pessoa, pergunta por pergunta, com o que ela marcou
 *   Resultados → média por pergunta, por setor, por tempo de casa e eNPS
 */

type Aba = "pesquisa" | "enviar" | "respostas" | "resultados";

/** O link que vai para o colaborador. */
export function linkClima(token: string): string {
  return `${window.location.origin}/clima/${token}`;
}

// O link é montado com o endereço por onde o RH foi aberto: abrindo pelo
// endereço do portal, os links já saem prontos para o colaborador (o código de
// cada pessoa é o mesmo em qualquer endereço).

/** Copia para a área de transferência; devolve se realmente conseguiu. */
export async function copiarTexto(texto: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(texto);
    return true;
  } catch {
    // Navegador sem permissão (ou http sem localhost): tenta o caminho antigo.
    try {
      const ta = document.createElement("textarea");
      ta.value = texto;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      ta.remove();
      return ok;
    } catch {
      return false;
    }
  }
}

function quando(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
}

function norm(s: unknown): string {
  return String(s ?? "")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .trim()
    .toLowerCase();
}

export default function ClimaPesquisaModal({
  id,
  colaboradores,
  onClose,
  onSaved,
}: {
  /** Sem id, o modal cria uma pesquisa nova. */
  id?: number;
  colaboradores: Row[];
  onClose: () => void;
  onSaved: (mensagem: string) => Promise<void> | void;
}) {
  const [aba, setAba] = useState<Aba>("pesquisa");
  const [pesquisa, setPesquisa] = useState<ClimaPesquisa | null>(null);

  const [titulo, setTitulo] = useState("Pesquisa de Clima Organizacional");
  const [periodo, setPeriodo] = useState(String(new Date().getFullYear()));
  const [identificada, setIdentificada] = useState(true);
  const [status, setStatus] = useState("Aberta");
  const [perguntas, setPerguntas] = useState<ClimaPergunta[]>(CLIMA_PERGUNTAS);

  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const carregar = useCallback(async (pesquisaId: number) => {
    try {
      const p = await api.clima.get(pesquisaId);
      setPesquisa(p);
      setTitulo(p.titulo);
      setPeriodo(p.periodo ?? "");
      setIdentificada(p.identificada);
      setStatus(p.status);
      setPerguntas(p.perguntas ?? []);
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Erro ao carregar a pesquisa.");
    }
  }, []);

  useEffect(() => {
    if (id) carregar(id);
  }, [id, carregar]);

  async function salvar() {
    setErro(null);
    if (!titulo.trim()) {
      setErro("Informe o título da pesquisa.");
      return;
    }
    setSalvando(true);
    try {
      const dados = {
        titulo: titulo.trim(),
        periodo: periodo.trim(),
        identificada,
        status,
        perguntas: perguntas.filter((p) => p.texto.trim()),
      };
      if (pesquisa) {
        await api.clima.update(pesquisa.id, dados);
        await carregar(pesquisa.id);
        await onSaved("Pesquisa atualizada.");
      } else {
        const r = await api.clima.create(dados);
        await carregar(r.id);
        setAba("enviar");
        await onSaved("Pesquisa criada — agora gere o link de cada colaborador.");
      }
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Não foi possível salvar.");
    } finally {
      setSalvando(false);
    }
  }

  const convites = pesquisa?.convites ?? [];
  const respostas = pesquisa?.respostas ?? [];

  return (
    <div className="modal-overlay" onMouseDown={onClose}>
      <div className="modal-card modal-clima" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div>
            <h3>{pesquisa ? pesquisa.titulo : "Nova pesquisa de clima"}</h3>
            <p className="modal-sub">
              Questionário oficial da SERCAMP · cada colaborador responde pelo próprio link
            </p>
          </div>
          <button type="button" className="icon-btn" onClick={onClose}>
            <IconClose size={18} />
          </button>
        </div>

        {pesquisa && (
          <div className="triagem-abas">
            <button className={aba === "pesquisa" ? "ativa" : ""} onClick={() => setAba("pesquisa")}>
              Pesquisa
            </button>
            <button className={aba === "enviar" ? "ativa" : ""} onClick={() => setAba("enviar")}>
              Enviar links ({convites.length})
            </button>
            <button className={aba === "respostas" ? "ativa" : ""} onClick={() => setAba("respostas")}>
              Respostas ({respostas.length})
            </button>
            <button className={aba === "resultados" ? "ativa" : ""} onClick={() => setAba("resultados")}>
              Resultados
            </button>
          </div>
        )}

        <div className="modal-body">
          {aviso && (
            <div className="endo-aviso">
              <span>{aviso}</span>
              <button className="icon-btn" title="Fechar" onClick={() => setAviso(null)}>
                <IconClose size={16} />
              </button>
            </div>
          )}

          {aba === "pesquisa" && (
            <AbaPesquisa
              titulo={titulo}
              setTitulo={setTitulo}
              periodo={periodo}
              setPeriodo={setPeriodo}
              identificada={identificada}
              setIdentificada={setIdentificada}
              status={status}
              setStatus={setStatus}
              perguntas={perguntas}
              setPerguntas={setPerguntas}
              existente={!!pesquisa}
            />
          )}

          {aba === "enviar" && pesquisa && (
            <AbaEnviar
              pesquisa={pesquisa}
              colaboradores={colaboradores}
              onMudou={async (msg) => {
                setAviso(msg);
                await carregar(pesquisa.id);
                await onSaved(msg);
              }}
            />
          )}

          {aba === "respostas" && pesquisa && <AbaRespostas pesquisa={pesquisa} />}

          {aba === "resultados" && pesquisa && <AbaResultados pesquisa={pesquisa} />}

          {erro && <div className="form-error">{erro}</div>}
        </div>

        <div className="modal-foot">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={salvando}>
            Fechar
          </button>
          {aba === "pesquisa" && (
            <button type="button" className="btn-primary" onClick={salvar} disabled={salvando}>
              {salvando ? "Salvando…" : pesquisa ? "Salvar alterações" : "Criar pesquisa"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ==================== Aba 1: a pesquisa e as perguntas ==================== */

function AbaPesquisa({
  titulo,
  setTitulo,
  periodo,
  setPeriodo,
  identificada,
  setIdentificada,
  status,
  setStatus,
  perguntas,
  setPerguntas,
  existente,
}: {
  titulo: string;
  setTitulo: (v: string) => void;
  periodo: string;
  setPeriodo: (v: string) => void;
  identificada: boolean;
  setIdentificada: (v: boolean) => void;
  status: string;
  setStatus: (v: string) => void;
  perguntas: ClimaPergunta[];
  setPerguntas: (p: ClimaPergunta[]) => void;
  existente: boolean;
}) {
  function alterar(i: number, texto: string) {
    setPerguntas(perguntas.map((p, x) => (x === i ? { ...p, texto } : p)));
  }

  return (
    <>
      <div className="doc-grid">
        <label className="field field-wide">
          <span>
            Título <span className="req">*</span>
          </span>
          <input value={titulo} onChange={(e) => setTitulo(e.target.value)} />
        </label>
        <label className="field">
          <span>Período</span>
          <input
            value={periodo}
            onChange={(e) => setPeriodo(e.target.value)}
            placeholder="Ex.: 2026 ou 2026 - 1º semestre"
          />
        </label>
        <label className="field">
          <span>Respostas</span>
          <select
            value={identificada ? "sim" : "nao"}
            onChange={(e) => setIdentificada(e.target.value === "sim")}
          >
            <option value="sim">Identificadas (o RH vê quem respondeu)</option>
            <option value="nao">Anônimas (só setor e tempo de empresa)</option>
          </select>
        </label>
        {existente && (
          <label className="field">
            <span>Situação</span>
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="Aberta">Aberta (recebendo respostas)</option>
              <option value="Encerrada">Encerrada</option>
            </select>
          </label>
        )}
      </div>

      <p className="clima-nota-sigilo">
        {identificada
          ? "Cada link é individual, então a resposta chega ligada ao colaborador — é o que permite tratar caso a caso. A página avisa isso a quem responde."
          : "As respostas chegam sem o nome: o RH só sabe quem já respondeu (status do link), não o que cada um escreveu."}
      </p>

      <div className="clima-escala-guia">
        <strong>Escala de avaliação</strong>
        <div className="clima-escala-itens">
          {CLIMA_ESCALA.map((n) => (
            <span key={n.valor}>
              <b>{n.valor}</b> {n.rotulo}
            </span>
          ))}
        </div>
      </div>

      <div className="triagem-perguntas-head">
        <span>
          Perguntas <small className="muted">({perguntas.length})</small>
        </span>
        <div>
          <button
            type="button"
            className="btn-secondary btn-sm"
            onClick={() =>
              setPerguntas([
                ...perguntas,
                {
                  id: `q${perguntas.length + 1}`,
                  texto: "",
                  tipo: "escala",
                  dimensao: "Geral",
                },
              ])
            }
          >
            <IconPlus size={15} /> Pergunta
          </button>
          <button
            type="button"
            className="btn-secondary btn-sm"
            onClick={() => setPerguntas([...CLIMA_PERGUNTAS])}
            title="Voltar ao questionário oficial da SERCAMP"
          >
            Restaurar oficial
          </button>
        </div>
      </div>

      <ol className="clima-perguntas-edit">
        {perguntas.map((p, i) => (
          <li key={p.id}>
            <div className="clima-pergunta-edit">
              <textarea
                rows={2}
                value={p.texto}
                onChange={(e) => alterar(i, e.target.value)}
                placeholder="Escreva a afirmação"
              />
              <div className="clima-pergunta-meta">
                <select
                  value={p.tipo}
                  onChange={(e) =>
                    setPerguntas(
                      perguntas.map((x, y) =>
                        y === i ? { ...x, tipo: e.target.value as ClimaPergunta["tipo"] } : x,
                      ),
                    )
                  }
                >
                  <option value="escala">Escala 1 a 5</option>
                  <option value="aberta">Resposta escrita</option>
                </select>
                <input
                  value={p.dimensao}
                  onChange={(e) =>
                    setPerguntas(perguntas.map((x, y) => (y === i ? { ...x, dimensao: e.target.value } : x)))
                  }
                  placeholder="Tema (ex.: Liderança)"
                />
                {p.enps && <span className="badge" title="Pergunta usada no eNPS">eNPS</span>}
                <button
                  type="button"
                  className="icon-btn icon-btn-danger"
                  title="Remover pergunta"
                  onClick={() => setPerguntas(perguntas.filter((_, y) => y !== i))}
                >
                  <IconTrash size={16} />
                </button>
              </div>
            </div>
          </li>
        ))}
      </ol>
    </>
  );
}

/* ==================== Aba 2: os links de cada colaborador ==================== */

function AbaEnviar({
  pesquisa,
  colaboradores,
  onMudou,
}: {
  pesquisa: ClimaPesquisa;
  colaboradores: Row[];
  onMudou: (mensagem: string) => Promise<void>;
}) {
  const podeExcluir = usePodeExcluir(); // remover o link apaga o convite gravado
  const [busca, setBusca] = useState("");
  const [gerando, setGerando] = useState(false);
  const [copiado, setCopiado] = useState<number | "todos" | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [setor, setSetor] = useState("");
  /**
   * FILA DE DISPARO. `null` = fila fechada. Aberta, ela guarda a ordem de quem
   * vai receber; `enviados` é quem já teve a conversa aberta (marcado também no
   * servidor, para o progresso não se perder ao fechar a tela nem se outra
   * pessoa do RH continuar o disparo).
   */
  const [fila, setFila] = useState<ClimaConvite[] | null>(null);
  const [enviados, setEnviados] = useState<Set<number>>(new Set());

  const setores = useMemo(() => {
    const s = new Set<string>();
    for (const c of colaboradores) {
      if (String(c.status ?? "") === "Ativo" && c.departamento) s.add(String(c.departamento));
    }
    return ordenarTextos([...s]);
  }, [colaboradores]);

  // Um link por pessoa: a lista (e tudo o que sai dela) em ordem alfabética.
  const convites = useMemo(
    () => ordenarPorNome(pesquisa.convites ?? [], (c) => c.nome),
    [pesquisa.convites],
  );
  const filtrados = useMemo(() => {
    const q = norm(busca);
    if (!q) return convites;
    return convites.filter((c) => norm(c.nome).includes(q) || norm(c.setor).includes(q));
  }, [convites, busca]);

  const semConvite = useMemo(() => {
    const jaTem = new Set(convites.map((c) => c.colaborador_id));
    return colaboradores.filter(
      (c) => String(c.status ?? "") === "Ativo" && !jaTem.has(Number(c.id)),
    ).length;
  }, [convites, colaboradores]);

  async function gerar(apenasSetor?: string) {
    setGerando(true);
    setErro(null);
    try {
      const r = await api.clima.gerarConvites(
        pesquisa.id,
        apenasSetor ? { departamento: apenasSetor } : {},
      );
      await onMudou(
        r.criados > 0
          ? `${r.criados} link(s) gerado(s).${r.ja_tinham > 0 ? ` ${r.ja_tinham} já tinham link.` : ""}`
          : "Todos os colaboradores desta seleção já tinham link.",
      );
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Não foi possível gerar os links.");
    } finally {
      setGerando(false);
    }
  }

  async function copiarUm(c: ClimaConvite) {
    const ok = await copiarTexto(linkClima(c.token));
    if (!ok) {
      setErro("Não consegui copiar sozinho — selecione o link e copie.");
      return;
    }
    setCopiado(c.id);
    setTimeout(() => setCopiado(null), 2500);
  }

  /** Lista "nome + link" de quem ainda não respondeu (para colar no WhatsApp/planilha). */
  async function copiarTodos() {
    const texto = convites
      .filter((c) => c.status !== "Respondido")
      .map((c) => `${c.nome ?? "—"}\t${linkClima(c.token)}`)
      .join("\n");
    if (!texto) {
      setErro("Todo mundo já respondeu — não há link pendente para copiar.");
      return;
    }
    const ok = await copiarTexto(texto);
    if (!ok) {
      setErro("Não consegui copiar a lista. Copie os links um a um.");
      return;
    }
    setCopiado("todos");
    setTimeout(() => setCopiado(null), 2500);
  }

  function mensagem(c: ClimaConvite): string {
    return (
      `Olá, ${c.nome ?? ""}! Aqui é o RH da SERCAMP.\n\n` +
      `Estamos realizando a *${pesquisa.titulo}*${pesquisa.periodo ? ` (${pesquisa.periodo})` : ""}. ` +
      `Sua opinião é muito importante para melhorarmos o ambiente de trabalho.\n\n` +
      `Este link é seu e leva poucos minutos:\n${linkClima(c.token)}`
    );
  }

  /** Abre a conversa do WhatsApp com a mensagem pronta (o RH só aperta enviar). */
  function abrirConversa(c: ClimaConvite) {
    const numero = String(c.telefone ?? "").replace(/\D/g, "");
    const base = numero
      ? `https://wa.me/${numero.length <= 11 ? `55${numero}` : numero}`
      : "https://wa.me/";
    window.open(`${base}?text=${encodeURIComponent(mensagem(c))}`, "_blank", "noopener");
  }

  async function abrirWhatsApp(c: ClimaConvite) {
    abrirConversa(c);
    await marcarEnviado(c);
  }

  /* ---------------- fila de disparo ---------------- */

  /** Tem número utilizável no cadastro? (DDD + número) */
  const temTelefone = (c: ClimaConvite) => String(c.telefone ?? "").replace(/\D/g, "").length >= 10;

  const pendentes = useMemo(() => convites.filter((c) => c.status !== "Respondido"), [convites]);
  const semTelefone = useMemo(() => pendentes.filter((c) => !temTelefone(c)).length, [pendentes]);

  /**
   * Monta a fila: só quem ainda não respondeu e tem telefone. Quem ainda não
   * recebeu vem primeiro — assim, retomando o disparo no dia seguinte, a fila
   * já começa de onde parou.
   */
  function abrirFila() {
    const lista = pendentes
      .filter(temTelefone)
      // Quem ainda não recebeu primeiro; dentro de cada grupo, A→Z.
      .sort(
        (a, b) =>
          Number(!!a.enviado_em) - Number(!!b.enviado_em) || compararNomes(a.nome, b.nome),
      );
    if (lista.length === 0) {
      setErro(
        semTelefone > 0
          ? "Ninguém da fila tem telefone no cadastro. Use o botão de copiar o link."
          : "Não há ninguém pendente: todo mundo já respondeu.",
      );
      return;
    }
    setErro(null);
    setFila(lista);
    setEnviados(new Set(lista.filter((c) => c.enviado_em).map((c) => c.id)));
  }

  /** Abre a conversa do próximo e marca no servidor (sem recarregar a tela). */
  async function dispararUm(c: ClimaConvite) {
    abrirConversa(c);
    setEnviados((atuais) => new Set(atuais).add(c.id));
    try {
      if (!c.enviado_em) await api.clima.marcarEnviado(c.id);
    } catch {
      /* o WhatsApp já abriu; a marcação é só acompanhamento */
    }
  }

  /** Fecha a fila e recarrega, para a tabela mostrar os "enviado em". */
  async function fecharFila() {
    setFila(null);
    await onMudou("Disparo encerrado.");
  }

  const proximo = fila?.find((c) => !enviados.has(c.id)) ?? null;

  async function abrirEmail(c: ClimaConvite) {
    const assunto = `${pesquisa.titulo}${pesquisa.periodo ? ` — ${pesquisa.periodo}` : ""}`;
    window.location.href =
      `mailto:${encodeURIComponent(c.email ?? "")}` +
      `?subject=${encodeURIComponent(assunto)}&body=${encodeURIComponent(mensagem(c))}`;
    await marcarEnviado(c);
  }

  async function marcarEnviado(c: ClimaConvite) {
    if (c.enviado_em) return;
    try {
      await api.clima.marcarEnviado(c.id);
      await onMudou("Link marcado como enviado.");
    } catch {
      /* marcar é só o acompanhamento: o link já foi aberto de qualquer forma */
    }
  }

  async function excluir(c: ClimaConvite) {
    if (
      !confirm(
        `Remover o link de ${c.nome ?? "este colaborador"}?\n\n` +
          `O link atual para de funcionar. Gere os links de novo para criar um novo.`,
      )
    )
      return;
    try {
      await api.clima.removerConvite(c.id);
      await onMudou("Link removido.");
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Não foi possível remover.");
    }
  }

  return (
    <>
      <p className="clima-como-envia">
        <strong>Como o colaborador recebe:</strong> clique em <strong>Gerar links</strong> — cada
        colaborador ativo ganha o link individual dele. Para mandar para todo mundo, use{" "}
        <strong>Disparar no WhatsApp</strong>: o sistema abre a conversa de um por vez, com a
        mensagem e o link já escritos — você só aperta enviar e ele já chama o próximo. Também dá
        para enviar um a um pela lista (WhatsApp, E-mail ou Copiar).
      </p>

      <div className="clima-enviar-topo">
        <div>
          <strong>Um link para cada colaborador.</strong>{" "}
          <span className="muted">
            {convites.length} link(s) gerado(s) ·{" "}
            {convites.filter((c) => c.status === "Respondido").length} respondido(s)
            {semConvite > 0 ? ` · ${semConvite} colaborador(es) ativo(s) ainda sem link` : ""}
          </span>
        </div>
        <div className="clima-enviar-acoes">
          <select value={setor} onChange={(e) => setSetor(e.target.value)}>
            <option value="">Todos os setores</option>
            {setores.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <button
            className="btn-primary btn-sm"
            disabled={gerando}
            onClick={() => gerar(setor || undefined)}
          >
            <IconLink size={15} /> {gerando ? "Gerando…" : "Gerar links"}
          </button>
          <button className="btn-secondary btn-sm" onClick={copiarTodos}>
            {copiado === "todos" ? <IconCheck size={15} /> : <IconCopy size={15} />}{" "}
            {copiado === "todos" ? "Copiado!" : "Copiar pendentes"}
          </button>
          {convites.length > 0 && !fila && (
            <button className="btn-whats btn-sm" onClick={abrirFila}>
              Disparar no WhatsApp ({pendentes.filter(temTelefone).length})
            </button>
          )}
        </div>
      </div>

      {fila && (
        <FilaDisparo
          fila={fila}
          enviados={enviados}
          proximo={proximo}
          semTelefone={semTelefone}
          onDisparar={dispararUm}
          onFechar={fecharFila}
        />
      )}

      {erro && <div className="form-error">{erro}</div>}

      <div className="search-wrap clima-busca">
        <IconSearch size={17} />
        <input
          className="search"
          placeholder="Buscar por nome ou setor..."
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
        />
      </div>

      {convites.length === 0 ? (
        <p className="muted">
          Nenhum link ainda. Escolha o setor (ou deixe "Todos") e clique em{" "}
          <strong>Gerar links</strong>: cada colaborador ativo ganha o link individual dele.
        </p>
      ) : (
        <div className="table-wrap">
          <table className="table clima-tabela-links">
            <thead>
              <tr>
                <th>Colaborador</th>
                <th>Setor</th>
                <th>Situação</th>
                <th>Link</th>
                <th className="col-actions">Enviar</th>
              </tr>
            </thead>
            <tbody>
              {filtrados.map((c) => (
                <tr key={c.id}>
                  <td>
                    <strong>{c.nome ?? "—"}</strong>
                    {c.cargo && <small className="muted"> · {c.cargo}</small>}
                  </td>
                  <td>{c.setor || "—"}</td>
                  <td>
                    <span className={`pill ${c.status === "Respondido" ? "pill-ok" : "pill-warn"}`}>
                      {c.status}
                    </span>
                    <small className="muted">
                      {c.status === "Respondido"
                        ? ` ${quando(c.respondido_em)}`
                        : c.enviado_em
                          ? ` enviado ${quando(c.enviado_em)}`
                          : " não enviado"}
                    </small>
                  </td>
                  <td className="clima-col-link">
                    <input readOnly value={linkClima(c.token)} onFocus={(e) => e.target.select()} />
                    <button
                      className="btn-secondary btn-sm"
                      onClick={() => copiarUm(c)}
                      title="Copiar o link desta pessoa"
                    >
                      {copiado === c.id ? <IconCheck size={15} /> : <IconCopy size={15} />}
                    </button>
                  </td>
                  <td className="col-actions">
                    <button
                      className="btn-whats btn-sm"
                      onClick={() => abrirWhatsApp(c)}
                      title={c.telefone ? `WhatsApp ${c.telefone}` : "Abrir o WhatsApp (sem número no cadastro)"}
                    >
                      WhatsApp
                    </button>
                    {c.email && (
                      <button className="btn-secondary btn-sm" onClick={() => abrirEmail(c)} title={c.email}>
                        E-mail
                      </button>
                    )}
                    {podeExcluir && (
                      <button
                        className="icon-btn icon-btn-danger"
                        title="Remover este link"
                        onClick={() => excluir(c)}
                      >
                        <IconTrash size={16} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

/* ==================== Fila de disparo no WhatsApp ==================== */

/**
 * O disparo em fila: o sistema abre a conversa de UM colaborador por vez, com a
 * mensagem e o link dele já escritos — o RH só aperta enviar no WhatsApp e o
 * botão já aponta para o próximo.
 *
 * Não é envio automático de propósito: mandar sozinho exige a API oficial da
 * Meta (número dedicado, modelo aprovado e custo por mensagem) ou uma API não
 * oficial, que costuma custar o bloqueio do número da empresa. Aqui sai do
 * próprio WhatsApp do RH, sem cadastro, sem custo e sem risco.
 */
function FilaDisparo({
  fila,
  enviados,
  proximo,
  semTelefone,
  onDisparar,
  onFechar,
}: {
  fila: ClimaConvite[];
  enviados: Set<number>;
  proximo: ClimaConvite | null;
  semTelefone: number;
  onDisparar: (c: ClimaConvite) => Promise<void>;
  onFechar: () => void;
}) {
  const feitos = fila.filter((c) => enviados.has(c.id)).length;

  return (
    <section className="clima-fila">
      <header>
        <strong>Disparo no WhatsApp</strong>
        <span className="badge">
          {feitos} de {fila.length} aberto(s)
        </span>
        <button className="btn-secondary btn-sm" onClick={onFechar}>
          Encerrar disparo
        </button>
      </header>

      <div className="endo-barra">
        <div
          className="endo-barra-fill"
          style={{ width: `${fila.length ? Math.round((feitos / fila.length) * 100) : 0}%` }}
        />
      </div>

      <div className="endo-disparo">
        {proximo ? (
          <>
            <button className="btn-whats" onClick={() => onDisparar(proximo)}>
              Abrir a conversa de {primeiroNome(proximo.nome)} →
            </button>
            <span className="muted">
              {proximo.setor || "sem setor"} · {proximo.telefone}
              {proximo.enviado_em ? " · já recebeu antes (reenvio)" : ""}
            </span>
          </>
        ) : (
          <span className="endo-ok">
            <IconCheck size={16} /> Todas as {fila.length} conversas foram abertas. Quem responder
            aparece na aba Respostas.
          </span>
        )}
      </div>

      <p className="muted clima-fila-dica">
        A conversa abre numa aba nova com a mensagem e o link prontos — é só apertar enviar no
        WhatsApp e voltar aqui para o próximo. Quem já respondeu fica fora da fila
        {semTelefone > 0
          ? `; ${semTelefone} pessoa(s) sem telefone no cadastro também ficam de fora (use o botão de copiar o link).`
          : "."}
      </p>

      <div className="endo-fila">
        {fila.map((c) => (
          <button
            key={c.id}
            className={`btn-whats btn-sm ${enviados.has(c.id) ? "ja-enviado" : ""}`}
            onClick={() => onDisparar(c)}
            title={`Abrir a conversa de ${c.nome ?? ""}`}
          >
            {enviados.has(c.id) && <IconCheck size={14} />} {primeiroNome(c.nome)}
          </button>
        ))}
      </div>
    </section>
  );
}

/** "Adolfo Oliveira Ribeiro" → "Adolfo" (cabe nos botões da fila). */
function primeiroNome(nome: string | null): string {
  return String(nome ?? "").trim().split(/\s+/)[0] || "colaborador";
}

/* ==================== Aba 3: as respostas, uma a uma ==================== */

function AbaRespostas({ pesquisa }: { pesquisa: ClimaPesquisa }) {
  const [busca, setBusca] = useState("");
  const respostas = pesquisa.respostas ?? [];

  const filtradas = useMemo(() => {
    const q = norm(busca);
    if (!q) return respostas;
    return respostas.filter(
      (r) =>
        norm(r.colaborador_nome).includes(q) ||
        norm(r.setor).includes(q) ||
        norm(r.comentario).includes(q),
    );
  }, [respostas, busca]);

  if (respostas.length === 0) {
    return (
      <p className="muted">
        Ninguém respondeu ainda. Mande os links da aba <strong>Enviar links</strong> — assim que
        alguém responder, a resposta completa aparece aqui.
      </p>
    );
  }

  return (
    <>
      <div className="search-wrap clima-busca">
        <IconSearch size={17} />
        <input
          className="search"
          placeholder="Buscar por colaborador, setor ou comentário..."
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
        />
      </div>

      <div className="clima-respostas">
        {filtradas.map((r) => (
          <RespostaCard key={r.id} resposta={r} identificada={pesquisa.identificada} />
        ))}
      </div>
    </>
  );
}

function RespostaCard({
  resposta,
  identificada,
}: {
  resposta: ClimaResposta;
  identificada: boolean;
}) {
  const [aberto, setAberto] = useState(false);
  const classe = classificacaoClima(resposta.media);

  return (
    <article className="clima-resposta">
      <header onClick={() => setAberto(!aberto)}>
        <div>
          <strong>
            {identificada ? resposta.colaborador_nome ?? "Sem nome" : "Resposta anônima"}
          </strong>
          <small className="muted">
            {" "}
            · {resposta.setor || "sem setor"} · {resposta.tempo_empresa || "tempo não informado"} ·{" "}
            {quando(resposta.respondido_em)}
          </small>
        </div>
        <span className={`pill pill-${classe.tom}`}>
          {resposta.media != null ? `${resposta.media.toFixed(2)}/5` : "—"} · {classe.rotulo}
        </span>
      </header>

      {aberto && (
        <div className="clima-resposta-corpo">
          <ol className="clima-respostas-lista">
            {resposta.respostas.map((item, i) => (
              <li key={item.id ?? i}>
                <p className="clima-pergunta-txt">{item.texto}</p>
                {item.tipo === "aberta" ? (
                  <p className="clima-resposta-txt">{item.resposta?.trim() || "— (não respondeu)"}</p>
                ) : item.nota == null ? (
                  <p className="clima-resposta-txt muted">— (não respondeu)</p>
                ) : (
                  <p className="clima-resposta-nota">
                    <span className="clima-marcado">{item.nota}</span>
                    <span>{item.rotulo ?? rotuloEscala(item.nota)}</span>
                    <span className="clima-barra">
                      <span style={{ width: `${(item.nota / 5) * 100}%` }} />
                    </span>
                  </p>
                )}
              </li>
            ))}
          </ol>
          <p className="muted clima-resposta-rodape">
            Pontuação {resposta.pontuacao ?? "—"}/100
            {resposta.enps != null
              ? ` · recomendação: ${
                  resposta.enps > 0 ? "promotor" : resposta.enps === 0 ? "neutro" : "detrator"
                }`
              : ""}{" "}
            · também lançada no histórico de clima (aba Lançamentos), onde a IA sugere ações.
          </p>
        </div>
      )}
    </article>
  );
}

/* ==================== Aba 4: o consolidado ==================== */

function AbaResultados({ pesquisa }: { pesquisa: ClimaPesquisa }) {
  const r = pesquisa.resultados;
  if (!r || r.geral.respostas === 0) {
    return <p className="muted">Os resultados aparecem aqui assim que chegarem as primeiras respostas.</p>;
  }
  const classe = classificacaoClima(r.geral.media);

  return (
    <div className="clima-resultados">
      <div className="endo-indicadores">
        <div className="endo-ind">
          <div className="endo-ind-valor">
            {r.geral.media != null ? r.geral.media.toFixed(2) : "—"}
          </div>
          <div className="endo-ind-rotulo">Média geral (1 a 5)</div>
          <div className="endo-ind-detalhe">
            {classe.rotulo} · {r.geral.pontuacao ?? "—"}/100 pontos
          </div>
        </div>
        <div className="endo-ind">
          <div className="endo-ind-valor">{r.geral.participacao ?? "—"}%</div>
          <div className="endo-ind-rotulo">Participação</div>
          <div className="endo-ind-detalhe">
            {r.geral.respostas} de {r.geral.convites} link(s) · {r.geral.pendentes} pendente(s)
          </div>
        </div>
        <div className="endo-ind">
          <div className="endo-ind-valor">{r.geral.enps ?? "—"}</div>
          <div className="endo-ind-rotulo">eNPS (indicaria a empresa)</div>
          <div className="endo-ind-detalhe">
            {r.geral.promotores} promotor(es) · {r.geral.neutros} neutro(s) ·{" "}
            {r.geral.detratores} detrator(es)
          </div>
        </div>
      </div>

      <section className="clima-bloco">
        <h4>Média por pergunta</h4>
        {r.porPergunta.map((p) => (
          <div className="clima-linha-media" key={p.id}>
            <div className="clima-linha-txt">
              <span>{p.texto}</span>
              <small className="muted">
                {p.dimensao} · {p.respostas} resposta(s)
              </small>
            </div>
            <div className="clima-linha-barra">
              <span style={{ width: `${((p.media ?? 0) / 5) * 100}%` }} />
            </div>
            <strong>{p.media != null ? p.media.toFixed(2) : "—"}</strong>
            <small className="clima-dist muted">
              Marcações:{" "}
              {[1, 2, 3, 4, 5]
                .map((n) => `${n} → ${p.distribuicao?.[String(n)] ?? 0}`)
                .join(" · ")}
            </small>
          </div>
        ))}
      </section>

      <div className="clima-duas-colunas">
        <section className="clima-bloco">
          <h4>Por setor</h4>
          {r.porSetor.length === 0 ? (
            <p className="muted">Sem dados.</p>
          ) : (
            r.porSetor.map((s) => (
              <div className="clima-linha-media" key={s.nome}>
                <div className="clima-linha-txt">
                  <span>{s.nome}</span>
                  <small className="muted">{s.respostas} resposta(s)</small>
                </div>
                <div className="clima-linha-barra">
                  <span style={{ width: `${((s.media ?? 0) / 5) * 100}%` }} />
                </div>
                <strong>{s.media != null ? s.media.toFixed(2) : "—"}</strong>
              </div>
            ))
          )}
        </section>

        <section className="clima-bloco">
          <h4>Por tempo de empresa</h4>
          {r.porTempoEmpresa.length === 0 ? (
            <p className="muted">Sem dados.</p>
          ) : (
            r.porTempoEmpresa.map((t) => (
              <div className="clima-linha-media" key={t.nome}>
                <div className="clima-linha-txt">
                  <span>{t.nome}</span>
                  <small className="muted">{t.respostas} resposta(s)</small>
                </div>
                <div className="clima-linha-barra">
                  <span style={{ width: `${((t.media ?? 0) / 5) * 100}%` }} />
                </div>
                <strong>{t.media != null ? t.media.toFixed(2) : "—"}</strong>
              </div>
            ))
          )}
        </section>
      </div>

      <section className="clima-bloco">
        <h4>O que a SERCAMP poderia fazer para melhorar ({r.comentarios.length})</h4>
        {r.comentarios.length === 0 ? (
          <p className="muted">Ninguém escreveu sugestões ainda.</p>
        ) : (
          <ul className="clima-comentarios">
            {r.comentarios.map((c, i) => (
              <li key={i}>
                <p className="clima-resposta-txt">{c.texto}</p>
                <small className="muted">
                  {pesquisa.identificada ? c.nome ?? "Sem nome" : "Anônimo"} ·{" "}
                  {c.setor || "sem setor"} · {quando(c.respondido_em)}
                </small>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
